import { observable, action, computed } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { DateFormatter } from '@estee/elc-formatters';
import { LoyaltyRepository } from '~repositories/LoyaltyRepository';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/LoyaltyService';
import { IServiceAppConfig } from '~domain/entities/IConfig';
import { emit, events } from '@estee/elc-service-bus';
import { AnalyticalTrackingUseCase } from '~use-cases/AnalyticalTrackingUseCase';
import { eventTag } from '~utils/Analytics';
import isValidDate from '~internal/utils/isValidDate';

export interface IConfigParamsDateFormats {
    defaultPointsHistoryDateFormat?: string;
    defaultAnniversaryDateFormat?: string;
}

export const transDatePlaceholderRegex = /::TRANS_DATE::/gi;

class LoyaltyPointsHistoryController extends ViewController {
    private dateFormatter: DateFormatter;
    @observable private configParams: IConfigParamsDateFormats;
    private loyaltyRepository: LoyaltyRepository;
    @observable public paginationLimit: number = 5;
    @observable public paginationConfigLimit: number = 0;

    public siteConfig: IServiceAppConfig;

    constructor(loyaltyRepository: LoyaltyRepository) {
        super();
        this.loyaltyRepository = loyaltyRepository;
        this.dateFormatter = diContainer.get(serviceNames.dateFormatter);
    }

    @action
    public loaded = async () => {
        this.isLoading = true;
        this.siteConfig = await diContainer.get(serviceNames.siteConfig);
        this.paginationLimit =
            this.siteConfig?.dashboard?.loyaltyLandingPageConfig
                ?.pointsHistory?.params?.pointsHistory
                ?.pointsHistoryPaginationLimit || 5;
        await this.loyaltyRepository.fetchPointsHistory();
        this.isLoading = false;
    };

    @action
    public onLoadMore = async (eLabel: string) => {
        this.isLoading = true;
        if (this.loyaltyRepository.enablePagination) {
            this.loyaltyRepository.setPointsHistoryPaginationData();
        } else {
            await this.loyaltyRepository.fetchPointsHistory();
        }
        emit(events.LOYALTY_LOADMORE_LINK_CLICKED);
        if (eLabel) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                { event_name: 'loyalty_transaction_history', event_action: 'cta_click', event_label: eLabel },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }
        this.isLoading = false;
    };

    @action
    public onLoadLess = async (ctaLabel: string) => {
        this.isLoading = true;
        if (this.loyaltyRepository.enablePagination) {
            this.loyaltyRepository.setPointsHistoryPaginationData(true, this.paginationLimit);
        } else {
            await this.loyaltyRepository.fetchPointsHistory();
        }
        if (ctaLabel) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                { event_name: 'loyalty_transaction_history', event_action: 'cta_click', event_label: ctaLabel },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }
        this.isLoading = false;
    };

    @action
    public getPaginationLimit(): number {
        const { pointsHistoryPaginationLimit = 5 } = this
            .siteConfig?.dashboard?.loyaltyLandingPageConfig
            ?.pointsHistory?.params?.pointsHistory || {};
        this.paginationConfigLimit = pointsHistoryPaginationLimit;
        this.paginationLimit += this.paginationConfigLimit;

        return this.paginationLimit;
    }

    public setConfigParams = (configParams: IConfigParamsDateFormats) => {
        this.configParams = configParams;
    };

    public formattedTransDate = (transDate: string, transDateMessage: string) => {
        const { defaultPointsHistoryDateFormat } = this.configs;

        return this.dateFormatter.formatDateWithPlaceholder({
            date: transDate,
            dateFormat: defaultPointsHistoryDateFormat,
            message: transDateMessage,
            placeholderRegex: transDatePlaceholderRegex
        });
    };

    @action
    public onOrderLinkClick = (transactionActivity: string, orderId: string) => {
        emit(events.LOYALTY_ONLINE_ORDER_NUMBER_CLICKED, transactionActivity);
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_transaction_history', event_action: 'reward_order_detail_click', event_label: `${transactionActivity}${orderId}` },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    };

    @action
    public onLinkClick = (eLabel: string) => {
        if (eLabel) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                { event_name: 'loyalty_transaction_history', event_action: 'cta_click', event_label: eLabel },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }
    };

    @computed
    public get anniversaryDate() {
        const nextAnnDateValue = this.loyaltyRepository?.loyaltyInfo?.anniversaryDate;

        return nextAnnDateValue && isValidDate(nextAnnDateValue)
            ? this.dateFormatter.formatDateWithPlaceholder({
                  message: '::LOYALTY_ANNIVERSARY_DATE::',
                  date: nextAnnDateValue,
                  dateFormat: this.configParams.defaultAnniversaryDateFormat,
                  placeholderRegex: /::LOYALTY_ANNIVERSARY_DATE::/gi
              })
            : null;
    }

    @computed
    public get data() {
        return {
            pointsHistoryInfo: this.loyaltyRepository?.pointsHistory
        };
    }

    @computed
    public get configs() {
        return this.configParams;
    }
}

export default LoyaltyPointsHistoryController;
